import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Card,
  CardContent,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  WHATSAPP_MARKETIN_RETRY_BROADCAST_ENDPOINT,
  TEMPLATE_BROADCAST_ENDPOINT,
} from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";
import axios from "axios";
import { ColorsConstants } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";
import { formatWithTzLabel } from "../../../utils/dateUtils";
import MessageNode from "../../Whatsapp/MessageNode";
import { getMpmProductCount, getMpmThumbnailImage, getSpmProductName } from "../../Whatsapp/mpmPreviewUtils";
import { authPreviewProps } from "../../Whatsapp/authTemplateUtils";
import WhatsAppDialogV2 from "../../Whatsapp/WhatsAppDialogV2";
import { ReactComponent as NowIcon } from "../../../assets/images/icons/campaign/broadcast/now.svg";
import { ReactComponent as ScheduleIcon } from "../../../assets/images/icons/campaign/broadcast/schedule.svg";
import AppStepper from "../../../common/app-stepper";
import AppDatePicker from "../../../common/app-date-picker";
import { ArrowBack } from "@mui/icons-material";
import BackButton from "../../../common/back-button";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "SUCCESS":
    case "COMPLETED":
      return "#28a745";
    case "FAILED":
    case "CANCELLED":
      return "#dc3545";
    case "PENDING":
    case "IN_PROGRESS":
      return "#ffc107";
    default:
      return "#6c757d";
  }
};

const getStatusLabel = (status) => {
  if (!status) return "N/A";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const datePickerPopperSx = {
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: ColorsConstants.primaryBlue100,
    color: "#fff",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClock-pin": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClockPointer-root": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClockPointer-thumb": {
    backgroundColor: ColorsConstants.primaryBlue100,
    borderColor: ColorsConstants.primaryBlue100,
  },
  "& .MuiClockNumber-root.Mui-selected": {
    backgroundColor: ColorsConstants.primaryBlue100,
    color: "#fff",
  },
  "& .MuiPickersActionBar-root .MuiButton-root": {
    color: ColorsConstants.primaryBlue100,
    fontWeight: 600,
  },
};

const cardStyle = {
  p: 2,
  borderRadius: 2,
  mb: 2,
  cursor: "pointer",
  backgroundColor: "#F6F6F6",
  transition: "all 0.2s ease",
  boxShadow: "none",
  border: "1px solid #E5E7EB",
  "&:hover": {
    borderColor: ColorsConstants.primaryBackgroundColor,
  },
};

const selectedCardStyle = {
  ...cardStyle,
  borderColor: ColorsConstants.primaryBackgroundColor,
  backgroundColor: "#F0F7FF",
  borderWidth: "2px",
};

const RetryFailedBroadcast = ({
  scheduleDate,
  setScheduleDate,
  broadcastTime,
  setBroadcastTime,
  onNavigate,
  onPreviewTemplate,
  ...props
}) => {
  const { getValidToken } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [summaryText, setSummaryText] = useState([]);
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [walletErrorDialog, setWalletErrorDialog] = useState({
    open: false,
    data: null,
  });
  const [expandedCards, setExpandedCards] = useState({});
  const [broadcastType, setBroadcastType] = useState("");
  const [updateSegment, setUpdateSegment] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCampaignData, setSelectedCampaignData] = useState(null);
  const [fetchedData, setFetchedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
  });
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState(null);

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Select Campaign", "Select Broadcast Type"];

  useEffect(() => {
    const now = new Date();

    // Set default to next full hour from current time
    const defaultScheduleTime = new Date(now);
    defaultScheduleTime.setHours(now.getHours() + 1, 0, 0, 0);

    if (!scheduleDate) {
      setScheduleDate(defaultScheduleTime);
    }

    if (!broadcastTime) {
      setBroadcastTime(defaultScheduleTime);
    }
  }, []);

  const getLatestBroadcastDateTime = () => {
    if (!selectedCampaign) return null;

    const selectedCampaignData = campaigns.find(
      (campaign) => campaign.id === selectedCampaign,
    );

    if (!selectedCampaignData) return null;

    const retries = selectedCampaignData.retries || [];

    // If there are retry attempts, get the latest retry's broadcastDateTime
    if (retries.length > 0) {
      const latestRetry = retries[retries.length - 1];
      return new Date(latestRetry.broadcastDateTime);
    }

    // If no retries, use the parent campaign's latestBroadcastDateTime
    return new Date(selectedCampaignData.latestBroadcastDateTime);
  };

  // Helper function to check if "Broadcast Now" should be disabled
  const isWithin24Hours = () => {
    const latestBroadcastTime = getLatestBroadcastDateTime();

    if (!latestBroadcastTime) return false;

    const now = new Date();
    const hoursSinceLastBroadcast =
      (now - latestBroadcastTime) / (1000 * 60 * 60);

    return hoursSinceLastBroadcast < 24;
  };

  // Helper function to get remaining hours in 24hr window
  const getRemainingHours = () => {
    const latestBroadcastTime = getLatestBroadcastDateTime();

    if (!latestBroadcastTime) return 0;

    const now = new Date();
    const hoursSinceLastBroadcast =
      (now - latestBroadcastTime) / (1000 * 60 * 60);

    if (hoursSinceLastBroadcast >= 24) return 0;

    return Math.ceil(24 - hoursSinceLastBroadcast);
  };

  // Update getMinimumScheduleDate to enforce 1-hour minimum
  const getMinimumScheduleDate = () => {
    const now = new Date();

    // Always enforce at least 1 hour from current time
    const oneHourFromNow = new Date(now);
    oneHourFromNow.setHours(now.getHours() + 1, 0, 0, 0); // Round to next full hour

    if (!selectedCampaign) return oneHourFromNow;

    const selectedCampaignData = campaigns.find(
      (campaign) => campaign.id === selectedCampaign,
    );

    if (!selectedCampaignData) return oneHourFromNow;

    const retries = selectedCampaignData.retries || [];

    // Get the latest broadcast datetime
    let latestDateTime;
    if (retries.length > 0) {
      const latestRetry = retries[retries.length - 1];
      latestDateTime = new Date(latestRetry.broadcastDateTime);
    } else {
      latestDateTime = new Date(selectedCampaignData.latestBroadcastDateTime);
    }

    // Calculate hours since last broadcast
    const hoursSinceLastBroadcast = (now - latestDateTime) / (1000 * 60 * 60);

    // If last broadcast is within 24 hours, minimum = last broadcast + 24 hours
    if (hoursSinceLastBroadcast < 24) {
      const minDate = new Date(latestDateTime);
      minDate.setHours(minDate.getHours() + 24);
      return minDate;
    }

    // If last broadcast is >= 24 hours ago, minimum = current time + 1 hour
    return oneHourFromNow;
  };

  useEffect(() => {
    if (scheduleDate && broadcastTime && broadcastType === "one-time") {
      // Create a new date from scheduleDate
      const date = new Date(scheduleDate);

      // If broadcastTime is a Date object, use its hours and minutes
      if (broadcastTime instanceof Date) {
        date.setHours(
          broadcastTime.getHours(),
          broadcastTime.getMinutes(),
          0,
          0,
        );
      }

      const formattedDate = date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      setSummaryText([
        `This broadcast will be sent on ${formattedDate} at ${formattedTime}.`,
      ]);
    } else {
      setSummaryText([]);
    }
  }, [scheduleDate, broadcastTime, broadcastType]);

  useEffect(() => {
    loadFailedBroadcastData();
  }, []);

  const handleTimeChange = (newTime) => {
    setBroadcastTime(newTime);
    validateTime(newTime);
  };

  const validateDate = (date) => {
    if (!date) return false;

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setDateError("Date cannot be in the past");
      return false;
    }

    setDateError("");
    return true;
  };

  const validateTime = (time) => {
    if (!time || !scheduleDate) return false;

    setTimeError("");
    return true;
  };

  const handleDateChange = (newDate) => {
    setScheduleDate(newDate);
    validateDate(newDate);
    if (broadcastTime) {
      validateTime(broadcastTime);
    }
  };

  // Disable past dates
  const shouldDisableDate = (date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  // Disable past times when today is selected
  const shouldDisableTime = (timeValue, clockType) => {
    if (!scheduleDate) return false;

    const selectedDate = new Date(scheduleDate);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() !== today.getTime()) {
      return false;
    }

    const now = new Date();
    const minimumTime = new Date();
    minimumTime.setHours(minimumTime.getHours() + 1, 0, 0, 0);

    if (clockType === "hours") {
      return timeValue < minimumTime.getHours();
    }

    if (clockType === "minutes") {
      if (
        broadcastTime &&
        broadcastTime.getHours() === minimumTime.getHours()
      ) {
        return timeValue < minimumTime.getMinutes();
      }
    }

    return false;
  };

  const loadFailedBroadcastData = () => {
    const tenant = localStorage.getItem("tenant");
    setLoading(true);
    setError(null);
    resetFormState();

    fetch(`${WHATSAPP_MARKETIN_RETRY_BROADCAST_ENDPOINT}`, {
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-ID": tenant,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch failed broadcasts");
        return res.json();
      })
      .then((res) => {
        setFetchedData(res);

        // Transform and sort data
        const transformedData = res.map((item, index) => ({
          id: item.id || index,
          campaignName: item.campaignName || "Unknown Campaign",
          latestBroadcastDateTime:
            item.latestBroadcastDateTime || "Unknown Date",
          templateName: item.templateName || "Unknown Template",
          templateType: item.templateType || "Unknown Type",
          failedCount: item.failedCount || 0,
          retries: (item.retries || []).map((attempt, attemptIndex) => ({
            id: attempt.id || `retry-${attemptIndex}`,
            campaignName:
              attempt.campaignName || `Retry #${attempt.retryCount}`,
            broadcastDateTime: attempt.broadcastDateTime || "Unknown Date",
            templateName: attempt.templateName || "Unknown Template",
            templateType: attempt.templateType || "Unknown Type",
            failedCount: attempt.failedCount || 0,
            status: attempt.retryBroadcastStatus || "N/A",
            retryCampaignId: attempt.retryCampaignId || null,
            retryCount: attempt.retryCount || attemptIndex + 1,
          })),
          originalData: item,
        }));

        // SORT WITH ROBUST DATE HANDLING
        const sortedCampaigns = transformedData.sort((a, b) => {
          const getLatestDate = (campaign) => {
            const safeDate = (dateStr) => {
              if (!dateStr || dateStr === "Unknown Date") return new Date(0); // Very old date
              const date = new Date(dateStr);
              return isNaN(date.getTime()) ? new Date(0) : date;
            };

            let latest = safeDate(campaign.latestBroadcastDateTime);

            if (campaign.retries?.length > 0) {
              campaign.retries.forEach((retry) => {
                const retryDate = safeDate(retry.broadcastDateTime);
                if (retryDate > latest) latest = retryDate;
              });
            }

            return latest;
          };

          return getLatestDate(b) - getLatestDate(a);
        });

        setCampaigns(sortedCampaigns);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";

    try {
      // Format: "Oct 07, 2025, 01:09 PM IST"
      return formatWithTzLabel(dateTimeString, "MMM DD, YYYY, hh:mm A");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const formatDate = (dateTimeString) => {
    if (!dateTimeString) return "N/A";

    try {
      // Format: "Jan 15, 2026 at 2:30 PM IST"
      return formatWithTzLabel(dateTimeString, "MMM DD, YYYY [at] hh:mm A");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const toggleExpand = (campaignId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [campaignId]: !prev[campaignId],
    }));
  };

  const handleBroadcastTypeChange = (event) => {
    setBroadcastType(event.target.value);
  };

  const handleCampaignSelection = (campaignId) => {
    setSelectedCampaign(campaignId);

    // Find the selected campaign
    const selectedCampaignData = campaigns.find((c) => c.id === campaignId);
    if (selectedCampaign) {
      setSelectedCampaignData(selectedCampaignData);
    }

    // Open dialog with template preview
    if (selectedCampaignData?.originalData?.template_preview) {
      setSelectedTemplate(selectedCampaignData.originalData);
      setSelectedTemplatePreview(
        selectedCampaignData.originalData.template_preview,
      );
      setDialogOpen(true);
    } else {
      console.warn(
        "⚠️ No template_preview found for selected campaign:",
        selectedCampaignData,
      );
    }
  };

  const handleDialogNext = (templateData) => {
    // This is called when Next button in dialog is clicked
    setDialogOpen(false);

    // Pass template_preview to parent for preview rendering
    if (onPreviewTemplate && templateData?.template_preview) {
      onPreviewTemplate(templateData.template_preview);
    }

    // Move to next step after campaign selection
    setActiveStep(1);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleAlertClose = () =>
    setAlertObj({ active: false, type: "", msg: "" });

  const resetFormState = () => {
    setSelectedCampaign(null);
    setBroadcastType("");
    setUpdateSegment(false);
    setExpandedCards({});
    setActiveStep(0);
  };

  const buildScheduledDateTime = () => {
    if (!scheduleDate || !broadcastTime) return null;

    const localDateTime = new Date(
      scheduleDate.getFullYear(),
      scheduleDate.getMonth(),
      scheduleDate.getDate(),
      broadcastTime.getHours(),
      broadcastTime.getMinutes(),
      0,
      0,
    );

    // Send the wall-clock value the tenant picked with no timezone suffix,
    // so the backend localizes it using the tenant's configured timezone
    // instead of either the browser's local zone or Django's global one.
    // (toISOString() would emit a UTC "Z" string here, which is wrong: the
    // picked hours/minutes are the tenant's local time, not UTC.)
    const pad = (n) => String(n).padStart(2, "0");
    const naiveDateTimeString =
      `${localDateTime.getFullYear()}-${pad(localDateTime.getMonth() + 1)}-${pad(localDateTime.getDate())}` +
      `T${pad(localDateTime.getHours())}:${pad(localDateTime.getMinutes())}:00`;

    return naiveDateTimeString;
  };

  const handleBroadcastClick = async () => {
    /* --------------------------------
     1. Required field validation
    ---------------------------------*/
    if (broadcastType === "one-time") {
      if (!scheduleDate || !broadcastTime) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please select both date and time for scheduled broadcast",
        });
        return;
      }
    }

    if (!selectedCampaign) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please select a campaign to broadcast",
      });
      return;
    }

    /* --------------------------------
      2. FINAL future-time guard
    ---------------------------------*/
    let scheduledAt = null;

    if (broadcastType === "one-time") {
      const minScheduleDate = getMinimumScheduleDate();
      scheduledAt = buildScheduledDateTime();
      if (!scheduledAt) return;

      const scheduled = new Date(scheduledAt);
      const now = new Date();

      if (scheduled <= now) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Scheduled time must be in the future",
        });
        return;
      }

      if (scheduled < minScheduleDate) {
        const latestBroadcastTime = getLatestBroadcastDateTime();
        const hoursSinceLastBroadcast = latestBroadcastTime
          ? (new Date() - latestBroadcastTime) / (1000 * 60 * 60)
          : 24;

        if (hoursSinceLastBroadcast < 24) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Scheduled broadcast must be at least 24 hours after the last broadcast attempt (minimum: ${formatDateTime(minScheduleDate)})`,
          });
        } else {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Scheduled time must be in the future",
          });
        }
        return;
      }
    }

    /* --------------------------------
      3. Campaign lookup
    ---------------------------------*/
    const selectedCampaignData = campaigns.find(
      (campaign) => campaign.id === selectedCampaign,
    );

    if (!selectedCampaignData) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Selected campaign data not found",
      });
      return;
    }

    if (broadcastType === "now" && isWithin24Hours()) {
      setAlertObj({
        active: true,
        type: "error",
        msg: `Please wait ${getRemainingHours()} more hour${
          getRemainingHours() !== 1 ? "s" : ""
        } before retrying this campaign with "Now" broadcast, or schedule it for later.`,
      });
      return;
    }

    /* --------------------------------
      4. 24-hour cooldown check
    ---------------------------------*/
    const hasRetryAttempts =
      selectedCampaignData.retries && selectedCampaignData.retries.length > 0;

    if (broadcastType === "now" && hasRetryAttempts) {
      const latestRetry =
        selectedCampaignData.retries[selectedCampaignData.retries.length - 1];

      const lastBroadcastTime = new Date(latestRetry.broadcastDateTime);
      const now = new Date();
      const hoursSinceLastBroadcast =
        (now - lastBroadcastTime) / (1000 * 60 * 60);

      if (hoursSinceLastBroadcast < 24) {
        const hoursRemaining = Math.ceil(24 - hoursSinceLastBroadcast);

        setAlertObj({
          active: true,
          type: "error",
          msg: `Please wait ${hoursRemaining} more hour${
            hoursRemaining !== 1 ? "s" : ""
          } before retrying this campaign again.`,
        });
        return;
      }
    }

    /* --------------------------------
      5. Submit
    ---------------------------------*/
    setBroadcastLoading(true);

    try {
      const payload = new FormData();

      payload.append("broadcast_type", broadcastType);

      if (broadcastType === "one-time") {
        payload.append("is_scheduled", "true");
        payload.append("scheduled_at", scheduledAt);
      } else {
        payload.append("is_scheduled", "false");
      }

      let campaignIdToUse;
      let parentCampaignId;
      let templateData;

      if (hasRetryAttempts) {
        const latestRetry =
          selectedCampaignData.retries[selectedCampaignData.retries.length - 1];

        campaignIdToUse = latestRetry.retryCampaignId;
        parentCampaignId = selectedCampaignData.id;
        templateData = selectedCampaignData.originalData?.template_data;
      } else {
        campaignIdToUse = selectedCampaignData.id;
        parentCampaignId = selectedCampaignData.id;
        templateData = selectedCampaignData.originalData?.template_data;
      }

      payload.append("is_retry_failed", "true");
      payload.append("campaign_id", campaignIdToUse.toString());
      payload.append("parent_campaign_id", parentCampaignId.toString());
      payload.append("update_segment", updateSegment.toString());

      if (templateData?.template_json) {
        payload.append(
          "template_json",
          JSON.stringify(templateData.template_json),
        );
      }

      if (templateData) {
        payload.append("template_data", JSON.stringify(templateData));
      }

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      await axios.post(TEMPLATE_BROADCAST_ENDPOINT, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": tenant,
        },
      });

      setAlertObj({
        active: true,
        type: "success",
        msg: "Failed broadcast retry initiated successfully!",
      });

      resetFormState();
      if (broadcastType === "now") {
        onNavigate("marketing_broadcast");
      } else {
        onNavigate("marketing_broadcast", { tab: 1 });
      }
    } catch (error) {
      console.error("Broadcast error:", error);

      const errorData = error.response?.data;

      // Check for wallet balance error
      if (
        errorData?.status === "error" &&
        errorData?.message?.toLowerCase().includes("wallet balance")
      ) {
        // Show detailed wallet error dialog
        setWalletErrorDialog({
          open: true,
          data: errorData,
        });
      } else {
        // Show generic error alert
        setAlertObj({
          active: true,
          type: "error",
          msg:
            errorData?.message || "Broadcast retry failed. Please try again.",
        });
      }
    } finally {
      setBroadcastLoading(false);
    }
  };

  const canRetryCampaign = (campaign) => {
    const retries = campaign.retries || [];

    // No retry yet → allow if original failed
    if (retries.length === 0) {
      return campaign.failedCount > 0;
    }

    const latestRetry = retries[retries.length - 1];

    // If last retry succeeded fully → no retry needed
    if ((latestRetry.failedCount || 0) === 0) {
      return false;
    }

    // If latest retry has failures, always enable
    return true;
  };

  const WalletErrorDialog = () => (
    <Dialog
      open={walletErrorDialog.open}
      onClose={() => setWalletErrorDialog({ open: false, data: null })}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorOutlineIcon sx={{ color: "#d32f2f", fontSize: 28 }} />
          <Typography sx={{ ...typography.h2, color: "#d32f2f" }}>
            Insufficient Wallet Balance
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          Your wallet does not have sufficient balance to complete this retry
          broadcast.
        </Alert>

        {/* Balance Overview */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ ...typography.bodySmall, color: "textSecondary", mb: 1 }}
          >
            Balance Overview
          </Typography>
          <Box
            sx={{
              backgroundColor: "#fef5e7",
              border: "1px solid #f39c12",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography sx={typography.bodySmall} color="textSecondary">
                Current Balance:
              </Typography>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                {formatCurrency(walletErrorDialog.data?.current_balance || 0)}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography sx={typography.bodySmall} color="textSecondary">
                Required Balance:
              </Typography>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                {formatCurrency(walletErrorDialog.data?.required_balance || 0)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                sx={{
                  ...typography.bodySmall,
                  fontWeight: 600,
                  color: "#d32f2f",
                }}
              >
                Shortfall:
              </Typography>
              <Typography sx={{ ...typography.h2, color: "#d32f2f" }}>
                {formatCurrency(walletErrorDialog.data?.shortfall || 0)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Broadcast Details */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ ...typography.bodySmall, color: "textSecondary", mb: 1 }}
          >
            Broadcast Details
          </Typography>
          <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 1, p: 2 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography sx={typography.bodySmall} color="textSecondary">
                Recipients:
              </Typography>
              <Typography sx={typography.bodySmall}>
                {walletErrorDialog.data?.retry_count || 0}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography sx={typography.bodySmall} color="textSecondary">
                Rate per Message:
              </Typography>
              <Typography sx={typography.bodySmall}>
                {formatCurrency(walletErrorDialog.data?.rate_per_message || 0)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={typography.bodySmall} color="textSecondary">
                Template Category:
              </Typography>
              <Typography sx={{ ...typography.bodySmall, fontWeight: 600 }}>
                {walletErrorDialog.data?.template_category || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Alert severity="info" icon={<AccountBalanceWalletIcon />}>
          Please recharge your wallet to continue with this broadcast.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <ButtonTwo
          onClick={() => setWalletErrorDialog({ open: false, data: null })}
          variant="outlined"
          sx={{
            borderColor: "#D1D5DB",
            minWidth: 85,
            color: "#6B7280",
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </ButtonTwo>
        <ButtonOne
          variant="contained"
          onClick={() => {
            setWalletErrorDialog({ open: false, data: null });
            // TODO: Navigate to wallet recharge page
            // navigate('/wallet/recharge');
          }}
          sx={{
            backgroundColor: "#007bff",
            "&:hover": { backgroundColor: "#0056b3" },
          }}
        >
          Recharge Wallet
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );

  // Render step content based on active step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ bgcolor: "transparent", p: 0 }}>
            <Grid container spacing={2}>
              {campaigns.map((campaign) => (
                <Grid item xs={12} key={campaign.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border:
                        selectedCampaign === campaign.id
                          ? `2px solid ${ColorsConstants.primaryBackgroundColor}`
                          : "1px solid #E5E7EB",
                      borderRadius: "12px",
                      cursor: canRetryCampaign(campaign)
                        ? "pointer"
                        : "default",
                      backgroundColor:
                        selectedCampaign === campaign.id
                          ? "#F0F7FF"
                          : "#FFFFFF",
                      transition:
                        "border-color 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": canRetryCampaign(campaign)
                        ? {
                            borderColor: ColorsConstants.primaryBackgroundColor,
                            boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
                          }
                        : {},
                    }}
                    onClick={() =>
                      canRetryCampaign(campaign) &&
                      handleCampaignSelection(campaign.id)
                    }
                  >
                    <CardContent
                      sx={{ px: 2.5, py: 2, "&:last-child": { pb: 2 } }}
                    >
                      {/* ── Row 1: Campaign Name + Failed Badge + Marketing Chip ── */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.75,
                        }}
                      >
                        {/* Left: Name + Red badge */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                          }}
                        >
                          <Typography
                            sx={{
                              ...typography.bodyLarge,
                              fontWeight: 700,
                              color: "#000000E5",
                            }}
                          >
                            {campaign.campaignName}
                          </Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              backgroundColor: "#DC2626",
                              color: "#fff",
                              ...typography.bodySmall,
                              fontWeight: 600,
                              px: 1.25,
                              py: 0.35,
                              borderRadius: "20px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {campaign.failedCount} failed messages
                          </Box>
                        </Box>

                        {/* Right: Template type chip with conditional styling - KEEPING ORIGINAL STYLES */}
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1.5,
                            py: 0.4,
                            borderRadius: "20px",
                            ...typography.bodySmall,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            ...(campaign.templateType?.toUpperCase() ===
                            "MARKETING"
                              ? {
                                  backgroundColor: "#1B76D11A",
                                  border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
                                  color: ColorsConstants.primaryBackgroundColor,
                                }
                              : campaign.templateType?.toUpperCase() ===
                                  "UTILITY"
                                ? {
                                    backgroundColor: "#FF98001A",
                                    border: "1px solid #FF7700",
                                    color: "#FF7700",
                                  }
                                : {
                                    backgroundColor: "#F3F4F6",
                                    border: "1px solid #D1D5DB",
                                    color: "#374151",
                                  }),
                          }}
                        >
                          {campaign.templateType}
                        </Box>
                      </Box>

                      {/* ── Row 2: Template Name ── */}
                      <Typography
                        sx={{
                          ...typography.bodySmall,
                          color: "#000000E5",
                          mb: 1,
                        }}
                      >
                        {campaign.templateName}
                      </Typography>

                      {/* ── Row 3: Last attempted + View retry history ── */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{ ...typography.bodySmall, color: "#6C7C93" }}
                        >
                          Last attempted:{" "}
                          {formatDate(campaign.latestBroadcastDateTime)}
                        </Typography>

                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(campaign.id);
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.4,
                            ...typography.bodySmall,
                            fontWeight: 500,
                            color: ColorsConstants.primaryPurple100,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          View retry history
                          {expandedCards[campaign.id] ? (
                            <ExpandLessIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          )}
                        </Box>
                      </Box>
                    </CardContent>

                    {/* ── Collapsible Retry History ── */}
                    <Collapse
                      in={expandedCards[campaign.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ mx: 2.5, mb: 0 }} />
                      <CardContent
                        sx={{
                          px: 2.5,
                          pt: 1.5,
                          pb: 2,
                          "&:last-child": { pb: 2 },
                        }}
                      >
                        {campaign.retries && campaign.retries.length > 0 ? (
                          campaign.retries.map((retry, index) => (
                            <Box
                              key={retry.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb:
                                  index < campaign.retries.length - 1
                                    ? 1.25
                                    : 0,
                              }}
                            >
                              {/* Retry label */}
                              <Typography
                                sx={{
                                  ...typography.bodySmall,
                                  color: "#777677",
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    ...typography.bodySmall,
                                    fontWeight: 600,
                                    color:
                                      ColorsConstants.primaryBackgroundColor,
                                  }}
                                >
                                  Retry Attempt # {retry.retryCount}:
                                </Typography>
                                {"  "}
                                {formatDate(retry.broadcastDateTime)}
                              </Typography>

                              {/* Status chip - KEEPING ORIGINAL STYLES */}
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  backgroundColor: getStatusColor(retry.status),
                                  color: "#fff",
                                  ...typography.bodySmall,
                                  fontWeight: 600,
                                  px: 1.25,
                                  py: 0.35,
                                  borderRadius: "20px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getStatusLabel(retry.status)}
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Typography
                            sx={{
                              ...typography.bodySmall,
                              color: "#6C7C93",
                              fontStyle: "italic",
                            }}
                          >
                            No retry attempts yet
                          </Typography>
                        )}
                      </CardContent>
                    </Collapse>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            {/* Left Column - Broadcast Type */}
            <Grid
              item
              xs={12}
              md={selectedTemplatePreview ? 8 : 12}
              sx={{
                bgcolor: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #DFE5EC",
                p: 3,
              }}
            >
              {/* Broadcast Type Box */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...typography.h2, mb: 2 }}>
                  Broadcast Type
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Option 1 - Now */}
                  <Paper
                    sx={{
                      ...(broadcastType === "now"
                        ? selectedCardStyle
                        : cardStyle),
                      flex: 1,
                      minWidth: 200,
                      maxWidth: "calc(50% - 8px)",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "8px",
                      p: 2,
                    }}
                    onClick={() =>
                      handleBroadcastTypeChange({ target: { value: "now" } })
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <NowIcon
                        style={{ width: 24, height: 24, color: "#4B5563" }}
                      />
                      <Radio
                        value="now"
                        checked={broadcastType === "now"}
                        sx={{
                          color: "#D1D5DB",
                          "&.Mui-checked": {
                            color: ColorsConstants.primaryBackgroundColor,
                          },
                          p: 0.5,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          ...typography.bodyLarge,
                          fontWeight: 600,
                          color: "#505050",
                          mb: "6px",
                        }}
                      >
                        Now
                      </Typography>
                      <Typography
                        sx={{
                          ...typography.bodySmall,
                          color: "#6B7280",
                        }}
                      >
                        Deliver immediately to all selected recipients
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Option 2 - One-time */}
                  <Paper
                    sx={{
                      ...(broadcastType === "one-time"
                        ? selectedCardStyle
                        : cardStyle),
                      flex: 1,
                      minWidth: 200,
                      maxWidth: "calc(50% - 8px)",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "8px",
                      p: 2,
                    }}
                    onClick={() =>
                      handleBroadcastTypeChange({
                        target: { value: "one-time" },
                      })
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <ScheduleIcon
                        style={{ width: 24, height: 24, color: "#4B5563" }}
                      />
                      <Radio
                        value="one-time"
                        checked={broadcastType === "one-time"}
                        sx={{
                          color: "#D1D5DB",
                          "&.Mui-checked": {
                            color: ColorsConstants.primaryBackgroundColor,
                          },
                          p: 0.5,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          ...typography.bodyLarge,
                          fontWeight: 600,
                          color: "#505050",
                          mb: "6px",
                        }}
                      >
                        Schedule
                      </Typography>
                      <Typography
                        sx={{
                          ...typography.bodySmall,
                          color: "#6B7280",
                        }}
                      >
                        Choose a specific date and time for delivery
                      </Typography>
                    </Box>
                  </Paper>
                </Box>

                {/* Conditional Content based on broadcastType */}
                {broadcastType === "now" && (
                  <Box
                    sx={{
                      mt: 3,
                      borderRadius: "12px",
                      p: 2,
                      background: "#ECF1FF",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        color: "#6C7C93",
                        fontSize: 20,
                        mt: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        ...typography.bodySmall,
                        color: "#6C7C93",
                        lineHeight: 1.5,
                      }}
                    >
                      This broadcast will be sent immediately to all selected
                      recipients.
                    </Typography>
                  </Box>
                )}

                {broadcastType === "one-time" && (
                  <>
                    {/* Date and Time Inputs */}
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <AppDatePicker
                            format="dd/MM/yyyy"
                            value={scheduleDate}
                            onChange={handleDateChange}
                            minDate={new Date()}
                            disableFuture={false}
                            error={!!dateError}
                            helperText={dateError}
                            placeholder="Select date"
                            fullWidth
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <TimePicker
                            label="Broadcast Time"
                            value={broadcastTime}
                            onChange={handleTimeChange}
                            slotProps={{
                              popper: { sx: datePickerPopperSx },
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: !!timeError,
                                helperText: timeError,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    height: 40,
                                    ...typography.bodyLarge,
                                    "& fieldset": {
                                      borderColor: "#DFE5EC",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#DFE5EC",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor:
                                        ColorsConstants.primaryBackgroundColor,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    ...typography.bodyLarge,
                                  },
                                },
                              },
                            }}
                            shouldDisableTime={shouldDisableTime}
                            ampm={false}
                            minutesStep={5}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>

                    {/* Summary Section */}
                    {summaryText.length > 0 && (
                      <Box
                        sx={{
                          mt: 3,
                          p: 2,
                          borderRadius: "12px",
                          background: "#ECF1FF",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <InfoOutlined
                          sx={{
                            color: "#6C7C93",
                            fontSize: 20,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          {summaryText.map((text, index) => (
                            <Typography
                              key={index}
                              sx={{
                                ...typography.bodySmall,
                                color: "#1B2232",
                                lineHeight: 1.5,
                                mb: index < summaryText.length - 1 ? 1 : 0,
                              }}
                            >
                              {text}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Selected Campaign Info */}
              {selectedCampaign && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#FFBB001A",
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                    Selected Campaign:{" "}
                    {
                      campaigns.find((c) => c.id === selectedCampaign)
                        ?.campaignName
                    }
                  </Typography>
                  <Typography sx={typography.bodySmall} color="textSecondary">
                    Template:{" "}
                    {
                      campaigns.find((c) => c.id === selectedCampaign)
                        ?.templateName
                    }{" "}
                    • Failed Messages:{" "}
                    {
                      campaigns.find((c) => c.id === selectedCampaign)
                        ?.failedCount
                    }
                  </Typography>
                </Box>
              )}

              {/* Alert Display */}
              {alertObj.active && (
                <Alert
                  severity={alertObj.type}
                  onClose={handleAlertClose}
                  sx={{ mt: 2 }}
                >
                  {alertObj.msg}
                </Alert>
              )}
            </Grid>

            {/* Right side - Message Preview */}
            {selectedTemplatePreview && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {selectedTemplatePreview.marketingOption === "carousel" ? (
                    <MessageNode
                      templateName={selectedTemplatePreview.templateName}
                      language={selectedTemplatePreview.language}
                      headerType="none"
                      headerText=""
                      headerFile={null}
                      body=""
                      sampleBodyFields={[]}
                      footer=""
                      mode="view"
                      allButtons={[]}
                      marketingOption="carousel"
                      carouselCards={selectedTemplatePreview.carouselCards}
                      carouselMainBody={
                        selectedTemplatePreview.carouselMainBody
                      }
                      carouselMainBodySampleFields={
                        selectedTemplatePreview.carouselMainBodySampleFields ||
                        []
                      }
                      {...Object.keys(selectedTemplatePreview)
                        .filter((key) => key.startsWith("cardFile_"))
                        .reduce((acc, key) => {
                          acc[key] = selectedTemplatePreview[key];
                          return acc;
                        }, {})}
                      sx={{
                        maxWidth: "100%",
                        overflowX: "hidden",
                        "& .MuiCard-root": {
                          maxWidth: "100%",
                        },
                        "& img": {
                          maxWidth: "100%",
                          height: "auto",
                        },
                      }}
                    />
                  ) : (
                    <MessageNode
                      templateName={
                        selectedTemplatePreview.template_name ||
                        selectedTemplatePreview.templateName
                      }
                      marketingOption={
                        selectedTemplatePreview.marketingOption || null
                      }
                      language={selectedTemplatePreview.language}
                      headerType={selectedTemplatePreview.headerType}
                      headerText={selectedTemplatePreview.headerText}
                      headerFile={selectedTemplatePreview.headerFile}
                      body={selectedTemplatePreview.body}
                      sampleBodyFields={
                        selectedTemplatePreview.sampleBodyFields
                      }
                      sampleHeaderFields={
                        selectedTemplatePreview.sampleHeaderFields
                      }
                      footer={selectedTemplatePreview.footer}
                      mode="view"
                      allButtons={selectedTemplatePreview.allButtons}
                      showSampleValues
                      location={selectedTemplatePreview.location}
                      productCount={getMpmProductCount(
                        selectedTemplatePreview.mpmSections,
                      )}
                      thumbnailImage={getMpmThumbnailImage(
                        selectedTemplatePreview.mpmThumbnail || selectedTemplatePreview.spmProduct,
                      )}
                      productName={getSpmProductName(selectedTemplatePreview.spmProduct)}
                      {...authPreviewProps(selectedTemplatePreview)}
                      sx={{
                        maxWidth: "100%",
                        overflowX: "hidden",
                        "& .MuiCard-root": {
                          maxWidth: "100%",
                        },
                        "& img": {
                          maxWidth: "100%",
                          height: "auto",
                        },
                      }}
                    />
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography sx={typography.bodyLarge}>
          Loading failed broadcasts...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography sx={{ ...typography.bodyLarge, color: "error.main" }}>
          Error: {error}
        </Typography>
        <ButtonOne onClick={loadFailedBroadcastData} sx={{ ml: 2 }}>
          Retry
        </ButtonOne>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {campaigns.length === 0 ? (
        <Typography
          sx={{ ...typography.bodyLarge, textAlign: "center", py: 4 }}
        >
          No failed broadcasts found.
        </Typography>
      ) : (
        <>
          {/* Stepper */}
          <Box sx={{ mb: 4, width: "100%" }}>
            <AppStepper steps={steps} activeStep={activeStep} />
          </Box>

          {/* Step Content */}
          <Box
            elevation={0}
            sx={{
              p: activeStep === 0 ? 0 : 3,
              minHeight: 400,
            }}
          >
            {renderStepContent()}
          </Box>

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              pt: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <ButtonTwo
                variant="outlined"
                onClick={() => onNavigate && onNavigate("marketing_broadcast")}
                sx={{
                  borderColor: "#D1D5DB",
                  color: "#6B7280",
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    backgroundColor: "#F9FAFB",
                  },
                }}
              >
                Cancel
              </ButtonTwo>
              {activeStep > 0 && <BackButton onClick={handleBack} />}
            </Box>
            <Box>
              {activeStep < steps.length - 1 ? (
                <ButtonOne
                  variant="contained"
                  onClick={handleNext}
                  disabled={!selectedCampaign}
                  sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}
                >
                  Next
                </ButtonOne>
              ) : (
                <ButtonOne
                  variant="contained"
                  onClick={handleBroadcastClick}
                  disabled={
                    !selectedCampaign ||
                    broadcastLoading ||
                    broadcastType === "" ||
                    (broadcastType === "one-time" &&
                      (!scheduleDate || !broadcastTime))
                  }
                  sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}
                >
                  {broadcastLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ color: "white", mr: 1 }}
                      />
                      Broadcasting...
                    </>
                  ) : (
                    "Broadcast Selected Campaign"
                  )}
                </ButtonOne>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* WhatsApp Dialog V2 */}
      <WhatsAppDialogV2
        open={dialogOpen}
        onClose={handleDialogClose}
        templateData={selectedTemplate}
        templatePreviewData={selectedTemplatePreview}
        onNext={handleDialogNext}
        mode="view"
      />

      <WalletErrorDialog />
    </Box>
  );
};

export default RetryFailedBroadcast;
